import * as p from "@clack/prompts";
import pc from "picocolors";
import { gatherStatus, type CarrierStatus } from "./status.js";
import {
  formatStatusBlock,
  formatNextSteps,
  oauthFirstUseNote,
  accountLinksNote,
  FLEET_NL_EXAMPLES,
} from "./guidance.js";
import { openUrl, SIGN_UP_URL, SIGN_IN_URL, CONSOLE_URL, ONBOARDING_URL } from "./urls.js";
import { carrierAsk } from "./ask.js";
import { resolveCliToken } from "./status.js";

export type HomeAction =
  | "refresh"
  | "install"
  | "signup"
  | "signin"
  | "console"
  | "onboarding"
  | "ask"
  | "site"
  | "exit";

function mark(ok: boolean): string {
  return ok ? pc.green("●") : pc.yellow("○");
}

/** Print status + always-on next steps (used by home and `carrier status`). */
export function printStatus(st: CarrierStatus): void {
  p.note(formatStatusBlock(st), "Status");
  p.note(formatNextSteps(st), "Next steps");
}

export async function showHomeStatus(): Promise<CarrierStatus> {
  const s = p.spinner();
  s.start("Checking Claude plugin + MCP registration");
  const st = await gatherStatus();
  s.stop("Status ready");
  printStatus(st);
  return st;
}

async function openAndReport(url: string, label: string): Promise<void> {
  const r = await openUrl(url);
  if (r.ok) p.log.success(r.hint);
  else {
    p.log.warn(r.hint);
    p.note(url, label);
  }
}

/**
 * First-class interactive home for non-developers.
 * Loops until the user exits or picks a command that continues outside the menu.
 */
export async function runHome(opts: {
  onInstall: () => Promise<void>;
  onSiteCreate: () => Promise<void>;
}): Promise<void> {
  let st = await showHomeStatus();
  p.note(oauthFirstUseNote(), "How auth works");

  for (;;) {
    const hasToken = !!resolveCliToken();
    const choice = await p.select({
      message: "What do you want to do?",
      options: [
        {
          value: "refresh" as const,
          label: `${mark(st.mcpRegistered && st.pluginInstalled)} Refresh status`,
          hint: "Re-check plugin + MCP",
        },
        {
          value: "install" as const,
          label: `${mark(st.pluginInstalled && st.mcpRegistered)} Install / re-register plugin + MCP`,
          hint: "Zero-cred URL · OAuth on first use",
        },
        {
          value: "signup" as const,
          label: "Open sign-up",
          hint: "Create a Carrier account (Google / GitHub / email)",
        },
        {
          value: "signin" as const,
          label: "Open sign-in",
          hint: "Existing account",
        },
        {
          value: "console" as const,
          label: "Open console",
          hint: CONSOLE_URL,
        },
        {
          value: "onboarding" as const,
          label: "Open onboarding (link OCS / managed setup)",
          hint: ONBOARDING_URL,
        },
        {
          value: "ask" as const,
          label: hasToken
            ? "Ask the fleet (carrier ask)"
            : "Ask the fleet (needs CARRIER_API_KEY)",
          hint: hasToken ? "Uses env token" : "Optional headless path",
        },
        {
          value: "site" as const,
          label: "Scaffold white-label storefront",
          hint: "carrier site create",
        },
        { value: "exit" as const, label: "Exit" },
      ],
    });

    if (p.isCancel(choice) || choice === "exit") {
      p.outro(pc.dim("Run `carrier` anytime for this menu. Talk to your fleet in Claude."));
      return;
    }

    switch (choice as HomeAction) {
      case "refresh":
        st = await showHomeStatus();
        break;
      case "install":
        await opts.onInstall();
        p.note(oauthFirstUseNote(), "OAuth on first use");
        st = await showHomeStatus();
        break;
      case "signup":
        await openAndReport(SIGN_UP_URL, "Sign-up URL");
        p.log.info("After sign-up, return here or open Claude and talk to Carrier — OAuth may already be done.");
        break;
      case "signin":
        await openAndReport(SIGN_IN_URL, "Sign-in URL");
        break;
      case "console":
        await openAndReport(CONSOLE_URL, "Console");
        break;
      case "onboarding":
        await openAndReport(ONBOARDING_URL, "Onboarding");
        p.log.info("Link OCS credentials (BYO) or finish managed setup, then use Claude MCP.");
        break;
      case "ask": {
        if (!resolveCliToken()) {
          p.log.warn("No CARRIER_API_KEY / OCS token in env.");
          p.note(
            [
              "Interactive (recommended): install MCP, open Claude, ask in plain English.",
              "Headless: Console → Settings → API Keys → export CARRIER_API_KEY=ak_…",
              "",
              "Example prompts:",
              ...FLEET_NL_EXAMPLES.slice(0, 3).map((e) => `  carrier ask "${e.prompt}"`),
            ].join("\n"),
            "How to ask",
          );
          break;
        }
        const intent = await p.text({
          message: "What should Carrier do?",
          placeholder: "show fleet health",
        });
        if (p.isCancel(intent) || !String(intent).trim()) break;
        const spin = p.spinner();
        spin.start("Asking Carrier MCP…");
        const result = await carrierAsk(String(intent).trim());
        spin.stop(result.ok ? "Answer" : "Could not complete ask");
        if (result.ok) p.note(result.text.slice(0, 4000), "carrier ask");
        else p.log.error(result.text);
        break;
      }
      case "site":
        await opts.onSiteCreate();
        return;
      default:
        break;
    }
  }
}

export { accountLinksNote };
